import React from 'react'
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import numeral from 'numeral'

import styles from './styles.module.scss'

import { getCurrency } from '../../../../helpers/currencies'

const formatNumber = (value, currency) => {
  return `${numeral(value).format('0,0.[00]')} ${currency}`
}

const SalesByHour = ({salesChart, type}) => {
  const { t } = useTranslation()

  const companyInfo = useSelector(state => state.company.companyInfo)
  const currency = getCurrency(companyInfo?.currency)

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <div className={styles.title}>
            {
              type === 'hourly' ? t('Sales by hour') : t('Sales by day')
            }
          </div>
          <div className={styles.description}>
            {
              t('Sales · checks · active employees. Yellow — peak hours, red — low productivity.')
            }
          </div>
        </div>
        <div className={styles.legend}>
          <div className={styles.legendItem}>
            <div className={styles.legendColor} style={{background: '#009c50'}} />
            <div className={styles.legendLabel}>{t('Sales')} {currency.symbol}</div>
          </div>
          <div className={styles.legendItem}>
            <div className={styles.legendColor} style={{background: '#0099e6'}} />
            <div className={styles.legendLabel}>{t('Checks')}</div>
          </div>
          <div className={styles.legendItem}>
            <div className={styles.legendColor} style={{background: '#da8b00'}} />
            <div className={styles.legendLabel}>{t('Employees')}</div>
          </div>
          <div className={styles.legendItem}>
            <div className={styles.legendColor} style={{background: '#f81901'}} />
            <div className={styles.legendLabel}>{t('Employees Cost')} {currency.symbol}</div>
          </div>
        </div>
      </div>
      <div className={styles.chartContainer}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={salesChart} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="salesGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="oklch(0.6 0.17 155)" stopOpacity={0.9} />
                <stop offset="100%" stopColor="oklch(0.6 0.17 155)" stopOpacity={0.4} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="oklch(0.92 0.008 240)" />
            {
              type === 'hourly'
                ? <XAxis dataKey="hour" stroke="oklch(0.5 0.02 250)" tickFormatter={(v) => `${v}:00`} />
                : <XAxis dataKey="date" stroke="oklch(0.5 0.02 250)" fontSize={12} />
            }
            
            <YAxis yAxisId="left" stroke="oklch(0.5 0.02 250)" />
            <YAxis yAxisId="right" orientation="right" stroke="oklch(0.5 0.02 250)" />
            <Tooltip
              contentStyle={{
                background: "oklch(1 0 0)",
                border: "1px solid oklch(0.92 0.008 240)",
                borderRadius: 8,
                fontSize: 12,
                color: '#009c50',
              }}
              formatter={(value, name, item) => {
                if (name === t('Employees Cost')) {
                  const sales = item?.payload?.sales;
                  const percentage = Number.isFinite(sales) && sales !== 0
                    ? ` (${numeral((value * 100) / sales).format('0,0.[00]')}%)`
                    : '';
                  return [`${formatNumber(value, currency.symbol)}${percentage}`, name];
                }
                if (name === t('Sales')) {
                  return [formatNumber(value, currency.symbol), name];
                }
                return [value, name];
              }}
              labelFormatter={(l) => type === 'hourly' ? `${l}:00` : l}
            />
            <Bar yAxisId="left" dataKey="sales" name={t('Sales')} fill="url(#salesGrad)" radius={[6, 6, 0, 0]} />
            <Line yAxisId="right" type="monotone" dataKey="bills" name={t('Bills')} stroke="oklch(0.65 0.16 240)" strokeWidth={2.5} dot={{ r: 3 }} />
            <Line yAxisId="right" type="monotone" dataKey="employees" name={t('Employees')} stroke="oklch(0.7 0.17 75)" strokeWidth={2.5} strokeDasharray="4 4" dot={{ r: 3 }} />
            <Line yAxisId="left" type="monotone" dataKey="employeesCost" name={t('Employees Cost')} stroke="oklch(0.6205 0.247 30.18)" strokeWidth={2.5} strokeDasharray="4 4" dot={{ r: 3 }} />
          </ComposedChart>
        </ResponsiveContainer>        
      </div>
    </div>
  )
}

export default SalesByHour
